<template>
  <div class="payment-methods">
    <!-- Quick periods -->
    <ReportChoices :choices="quickRanges" :active="activeRange" @select="applyRange" />

    <!-- Filters + actions -->
    <div class="rp-toolbar">
      <div class="rp-filters" style="--rp-cols: 3">
        <div class="rp-filter">
          <label>From date</label>
          <input v-model="filters.date_from" type="date" class="form-control" :max="filters.date_to || today" />
        </div>
        <div class="rp-filter">
          <label>To date</label>
          <input v-model="filters.date_to" type="date" class="form-control" :min="filters.date_from" :max="today" />
        </div>
        <div class="rp-filter">
          <label>Customer</label>
          <FormDropdown v-model="filters.customer" :options="customerOptions" placeholder="All customers" clearable />
        </div>
        <div class="rp-filter">
          <label>Sold by</label>
          <FormDropdown v-model="filters.cashier" :options="cashierOptions" placeholder="Everyone" clearable />
        </div>
      </div>

      <ReportActions
        :loading="loading"
        :exporting="exporting"
        :can-reset="hasActiveFilters"
        :can-print="!!rows.length"
        @reset="resetFilters"
        @export="exportFile"
        @print="printReport"
      />
    </div>

    <div v-if="error" class="alert alert-danger d-flex align-items-center gap-2">
      <i class="ti ti-alert-circle"></i>{{ error }}
    </div>

    <!-- Summary cards -->
    <div class="rp-cards" :class="{ 'is-loading': loading }">
      <div class="rp-card tone-orange">
        <span class="rp-card-icon"><i class="ti ti-receipt-2"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Received at Sale</div>
          <div class="rp-card-value">{{ formatMoney(summary.received_at_sale) }}</div>
          <div class="rp-card-sub">of {{ formatMoney(summary.sales) }} sales · {{ plural(summary.invoice_count, 'invoice') }}</div>
        </div>
      </div>
      <div class="rp-card tone-red">
        <span class="rp-card-icon"><i class="ti ti-clock-dollar"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Left on Due</div>
          <div class="rp-card-value">{{ formatMoney(summary.on_due) }}</div>
          <div class="rp-card-sub">{{ formatMoney(summary.collected) }} due collected · {{ plural(summary.collection_count, 'receipt') }}</div>
        </div>
      </div>
      <button type="button" class="rp-card rp-card-action tone-blue" :class="{ selected: filters.mode === 'split' }"
              :title="filters.mode === 'split' ? 'Show all invoices' : 'Show split payments'" @click="toggleSplit">
        <span class="rp-card-icon"><i class="ti ti-arrows-split-2"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Split Payments</div>
          <div class="rp-card-value">{{ formatInt(summary.split_count) }}</div>
          <div class="rp-card-sub">{{ summary.split_rate }}% of invoices · {{ formatMoney(summary.split_amount) }}</div>
        </div>
      </button>
      <div class="rp-card tone-green">
        <span class="rp-card-icon"><i class="ti ti-wallet"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Net Received</div>
          <div class="rp-card-value">{{ formatMoney(summary.received) }}</div>
          <div class="rp-card-sub">after {{ formatMoney(summary.refunded) }} refunded on {{ plural(summary.refund_count, 'return') }}</div>
        </div>
      </div>
    </div>

    <!-- Insights -->
    <div class="rp-insights trend-first" :class="{ 'is-loading': loading }">
      <div class="rp-panel">
        <div class="rp-panel-title d-flex justify-content-between align-items-center">
          <span>Sales by payment type {{ summary.trend_unit === 'month' ? 'by month' : 'by day' }}</span>
          <span class="rp-legend">
            <span v-for="kind in trendKinds" :key="kind.kind"><i class="rp-dot" :style="{ background: kind.color }"></i>{{ kind.label }}</span>
          </span>
        </div>
        <div v-show="summary.trend.length" class="rp-chart"><canvas ref="trendChart"></canvas></div>
        <p v-if="!summary.trend.length" class="text-muted small mb-0">No sales in this period.</p>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">By payment type</div>
        <div v-if="kindBars.length" class="rp-bars">
          <div v-for="kind in kindBars" :key="kind.kind">
            <div class="d-flex justify-content-between small mb-1">
              <span class="text-truncate me-2"><i class="ti me-1" :class="PAYMENT_ICONS[kind.kind]"></i>{{ kind.label }}</span>
              <span class="fw-semibold text-nowrap">
                {{ formatMoney(kind.sales) }} <span class="text-muted fw-normal">({{ kind.share }}%)</span>
              </span>
            </div>
            <div class="rp-bar-track">
              <div class="rp-bar-fill" :style="{ width: `${kind.percent}%`, background: PAYMENT_COLORS[kind.kind] }"></div>
            </div>
          </div>
        </div>
        <p v-else class="text-muted small mb-0">No payments in this period.</p>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">Split payment combinations</div>
        <ul v-if="summary.combinations.length" class="rp-ranked">
          <li v-for="(combo, index) in summary.combinations.slice(0, 5)" :key="combo.label">
            <span class="rp-rank">{{ index + 1 }}</span>
            <div class="flex-grow-1 min-w-0">
              <div class="text-truncate" :title="combo.label">{{ combo.label }}</div>
              <small class="text-muted">{{ plural(combo.count, 'invoice') }}</small>
            </div>
            <span class="fw-semibold text-nowrap">{{ formatMoney(combo.amount) }}</span>
          </li>
        </ul>
        <p v-else class="text-muted small mb-0">No invoice was split over several methods.</p>
      </div>
    </div>

    <!-- Methods table -->
    <div class="rp-table-card">
      <div class="rp-table-head">
        <div class="rp-pills">
          <button v-for="pill in modePills" :key="pill.key" type="button" class="rp-pill"
                  :class="{ active: (filters.mode || 'all') === pill.key }" @click="setMode(pill.key)">
            {{ pill.label }} <span class="rp-pill-count">{{ formatInt(pill.count) }}</span>
          </button>
        </div>
        <div class="d-flex align-items-center gap-2 small text-muted">
          Show
          <select v-model.number="pageSize" class="form-select form-select-sm rp-page-size">
            <option v-for="size in PAGE_SIZES" :key="size" :value="size">{{ size }}</option>
          </select>
          per page
        </div>
      </div>

      <div class="table-responsive rp-table-wrap">
        <div v-if="loading" class="rp-loading"><span class="spinner-border text-warning"></span></div>
        <table class="table rp-table mb-0">
          <thead>
            <tr>
              <th class="rp-expand-col"></th>
              <th v-for="column in columns" :key="column.key" :class="column.class" @click="sortBy(column.key)">
                <span class="rp-sortable" :class="{ active: sortKey === column.key }">
                  {{ column.label }}
                  <i class="ti" :class="sortIcon(column.key)"></i>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="row in pagedRows" :key="row.key">
              <tr :class="{ expanded: expanded[row.key] }">
                <td class="rp-expand-col">
                  <button v-if="row.invoices.length" type="button" class="rp-expand"
                          :title="expanded[row.key] ? 'Hide invoices' : 'Show invoices'" @click="toggleRow(row.key)">
                    <i class="ti" :class="expanded[row.key] ? 'ti-chevron-down' : 'ti-chevron-right'"></i>
                  </button>
                </td>
                <td>
                  <div class="d-flex align-items-center gap-2">
                    <span class="pm-icon" :style="{ '--pm': PAYMENT_COLORS[row.kind] || '#9AA1A9' }">
                      <i class="ti" :class="PAYMENT_ICONS[row.kind] || 'ti-credit-card'"></i>
                    </span>
                    <span class="fw-semibold">{{ row.label }}</span>
                  </div>
                </td>
                <td><span class="rp-badge" :class="PAYMENT_TONES[row.kind] || 'muted'">{{ row.kind_label }}</span></td>
                <td class="text-center">
                  <span class="rp-count" :class="{ zero: !row.invoice_count }">{{ row.invoice_count }}</span>
                  <span v-if="row.split_count" class="rp-sub">{{ row.split_count }} split</span>
                </td>
                <td class="text-end fw-semibold">{{ Number(row.sales) ? formatMoney(row.sales) : '—' }}</td>
                <td class="text-end pm-share">
                  {{ row.share }}%
                  <div class="rp-bar-track mt-1">
                    <div class="rp-bar-fill" :style="{ width: `${Math.min(100, Number(row.share))}%`, background: PAYMENT_COLORS[row.kind] }"></div>
                  </div>
                </td>
                <td class="text-end" :class="{ 'text-muted': !Number(row.collected) }">
                  {{ Number(row.collected) ? `+${formatMoney(row.collected)}` : '—' }}
                  <span v-if="row.collection_count" class="rp-sub">{{ plural(row.collection_count, 'receipt') }}</span>
                </td>
                <td class="text-end" :class="Number(row.refunded) ? 'text-danger' : 'text-muted'">
                  {{ Number(row.refunded) ? `−${formatMoney(row.refunded)}` : '—' }}
                  <span v-if="row.refund_count" class="rp-sub">{{ plural(row.refund_count, 'return') }}</span>
                </td>
                <td class="text-end fw-semibold">
                  <template v-if="row.kind === 'due'"><span class="text-muted fw-normal">not received</span></template>
                  <template v-else>{{ formatMoney(row.received) }}</template>
                </td>
              </tr>

              <tr v-if="expanded[row.key]" class="rp-detail-row">
                <td></td>
                <td :colspan="columns.length">
                  <div class="rp-detail">
                    <div class="rp-detail-title">
                      <i class="ti me-1" :class="PAYMENT_ICONS[row.kind] || 'ti-credit-card'"></i>Invoices paid with {{ row.label }}
                    </div>
                    <div class="table-responsive">
                      <table class="table table-sm mb-0">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Invoice</th>
                            <th>Customer</th>
                            <th class="text-end">Paid with {{ row.label }}</th>
                            <th class="text-end">Invoice Total</th>
                            <th>Together with</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="invoice in row.invoices" :key="invoice.invoice_no">
                            <td class="text-nowrap">{{ formatDate(invoice.date) }} <small class="text-muted">{{ invoice.time }}</small></td>
                            <td class="fw-semibold">{{ invoice.invoice_no }}</td>
                            <td>{{ invoice.customer }}</td>
                            <td class="text-end fw-semibold">{{ formatMoney(invoice.amount) }}</td>
                            <td class="text-end">{{ formatMoney(invoice.total) }}</td>
                            <td :class="{ 'text-muted': !invoice.with }">{{ invoice.with || 'Single method' }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </td>
              </tr>
            </template>

            <tr v-if="!loading && !pagedRows.length">
              <td :colspan="columns.length + 1">
                <div class="rp-empty">
                  <i class="ti ti-credit-card"></i>
                  <div class="fw-semibold">No payments found</div>
                  <small class="text-muted">Try another period, customer or cashier.</small>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="rows.length">
            <tr>
              <th></th>
              <th colspan="2" class="text-end">Total ({{ plural(rows.length, 'method') }})</th>
              <th class="text-center">{{ formatInt(summary.invoice_count) }}</th>
              <th class="text-end">{{ formatMoney(summary.sales) }}</th>
              <th></th>
              <th class="text-end">{{ formatMoney(summary.collected) }}</th>
              <th class="text-end text-danger">{{ Number(summary.refunded) ? `−${formatMoney(summary.refunded)}` : '—' }}</th>
              <th class="text-end">{{ formatMoney(summary.received) }}</th>
            </tr>
          </tfoot>
        </table>
      </div>

      <ReportPagination
        v-if="visibleRows.length"
        :page="page"
        :page-count="pageCount"
        :pages="pageNumbers"
        :start="pageStart"
        :end="pageEnd"
        :total="visibleRows.length"
        @go="goToPage"
      />
    </div>
    <p class="rp-hint mt-2 mb-0">
      Net received = sales paid with the method + due collected through it − refunds paid from it. Due collections and
      refunds are left out when only single-method or split invoices are shown.
    </p>
  </div>
</template>

<script>
import apiUrls from '@/urls';
import inventoryReport, { escapeHtml } from '../inventory/inventoryReportMixin';
import periodReport, { defaultPeriod } from '../inventory/periodReportMixin';
import salesReport, { PAYMENT_COLORS, PAYMENT_ICONS, PAYMENT_TONES } from './salesReportMixin';

const EMPTY_SUMMARY = {
  sales: 0, collected: 0, refunded: 0, received: 0, invoice_total: 0, received_at_sale: 0, on_due: 0,
  invoice_count: 0, method_count: 0, split_count: 0, split_amount: 0, split_rate: 0, collection_count: 0,
  refund_count: 0, by_kind: [], combinations: [], mode_counts: { all: 0, single: 0, split: 0 }, trend_unit: 'day',
  trend: [],
};
const KIND_LABELS = { cash: 'Cash', bank: 'Bank', card: 'Card', mobile: 'Mobile banking', due: 'Due' };

export default {
  name: 'PaymentMethodReport',
  mixins: [inventoryReport, periodReport, salesReport],

  reportUrl: apiUrls.payment_methods_report.get,
  fileName: 'payment_methods',
  columns: [
    { key: 'label', label: 'Method' },
    { key: 'kind_label', label: 'Type' },
    { key: 'invoice_count', label: 'Invoices', class: 'text-center', numeric: true },
    { key: 'sales', label: 'Sales', class: 'text-end', numeric: true },
    { key: 'share', label: 'Share', class: 'text-end', numeric: true },
    { key: 'collected', label: 'Due Collected', class: 'text-end', numeric: true },
    { key: 'refunded', label: 'Refunds', class: 'text-end', numeric: true },
    { key: 'received', label: 'Net Received', class: 'text-end', numeric: true },
  ],

  data() {
    return {
      filters: {
        ...defaultPeriod(), customer: null, cashier: null, mode: null,
        warehouse: null, category: null, sub_category: null, search: '',
      },
      summary: { ...EMPTY_SUMMARY },
      sortKey: null,
      sortDir: 'desc',
      PAYMENT_COLORS,
      PAYMENT_ICONS,
      PAYMENT_TONES,
    };
  },

  computed: {
    hasActiveFilters() {
      const { customer, cashier, mode } = this.filters;
      return this.periodChanged || !!(customer || cashier || mode);
    },

    modePills() {
      const counts = this.summary.mode_counts;
      return [
        { key: 'all', label: 'All invoices', count: counts.all },
        { key: 'single', label: 'Single method', count: counts.single },
        { key: 'split', label: 'Split payments', count: counts.split },
      ];
    },

    kindBars() {
      const total = this.summary.by_kind.reduce((sum, k) => sum + (Number(k.sales) || 0), 0);
      return this.withPercent(this.summary.by_kind.filter((k) => Number(k.sales)), 'sales').map((k) => ({
        ...k, share: total ? Math.round(((Number(k.sales) || 0) / total) * 100) : 0,
      }));
    },

    /** Payment types that appear in the trend, in the server's order. */
    trendKinds() {
      return Object.keys(KIND_LABELS)
        .filter((kind) => this.summary.trend.some((t) => Number(t[kind])))
        .map((kind) => ({ kind, label: KIND_LABELS[kind], color: PAYMENT_COLORS[kind] }));
    },

    visibleRows() {
      return this.sortKey ? this.sortRows(this.rows) : this.rows;
    },
  },

  watch: {
    'filters.cashier'() { this.onFilterChange(); },
    'filters.mode'() { this.onFilterChange(); },
  },

  methods: {
    extraParams() {
      const { cashier, mode } = this.filters;
      return {
        ...this.periodParams(),
        ...this.customerParams(),
        ...(cashier ? { cashier } : {}),
        ...(mode ? { mode } : {}),
      };
    },

    resetExtraFilters() {
      this.sortKey = null;
      return { ...defaultPeriod(this.today), customer: null, cashier: null, mode: null };
    },

    applyResult(data) {
      this.summary = { ...EMPTY_SUMMARY, ...(data?.summary || {}) };
      const trend = this.summary.trend;
      this.$nextTick(() => this.drawBars('trend', this.$refs.trendChart, trend.map((t) => this.trendLabel(t.date)),
        this.trendKinds.map((k) => ({ label: k.label, data: trend.map((t) => t[k.kind]), color: k.color })),
        { stacked: true }));
    },

    setMode(key) {
      this.filters.mode = key === 'all' ? null : key;
    },

    toggleSplit() {
      this.filters.mode = this.filters.mode === 'split' ? null : 'split';
    },

    printReport() {
      const s = this.summary;
      const body = this.visibleRows.map((r) => `
        <tr>
          <td>${escapeHtml(r.label)}</td>
          <td>${escapeHtml(r.kind_label)}</td>
          <td class="num">${r.invoice_count}${r.split_count ? ` (${r.split_count} split)` : ''}</td>
          <td class="num">${this.formatMoney(r.sales)}</td>
          <td class="num">${r.share}%</td>
          <td class="num">${this.formatMoney(r.collected)}</td>
          <td class="num">${this.formatMoney(r.refunded)}</td>
          <td class="num">${r.kind === 'due' ? '—' : this.formatMoney(r.received)}</td>
        </tr>`).join('');
      const table = `<table><thead><tr><th>Method</th><th>Type</th><th class="num">Invoices</th><th class="num">Sales</th>
          <th class="num">Share</th><th class="num">Due Collected</th><th class="num">Refunds</th>
          <th class="num">Net Received</th></tr></thead>
        <tbody>${body}</tbody>
        <tfoot><tr><td colspan="2" class="num">Total</td><td class="num">${s.invoice_count}</td>
          <td class="num">${this.formatMoney(s.sales)}</td><td></td><td class="num">${this.formatMoney(s.collected)}</td>
          <td class="num">${this.formatMoney(s.refunded)}</td><td class="num">${this.formatMoney(s.received)}</td></tr></tfoot></table>`;
      const mode = this.modePills.find((p) => p.key === (this.filters.mode || 'all'))?.label;
      this.openPrint(
        'Payment Method Report',
        [
          [this.periodText(), `Invoices: ${mode}`, this.customerText(), this.cashierText(), this.storeText()].join(' · '),
          `Printed ${new Date().toLocaleString()}`,
        ],
        [
          ['Received at Sale', this.formatMoney(s.received_at_sale)],
          ['Left on Due', this.formatMoney(s.on_due)],
          ['Split Payments', `${this.formatInt(s.split_count)} (${s.split_rate}%)`],
          ['Net Received', this.formatMoney(s.received)],
        ],
        table,
      );
    },
  },
};
</script>

<style scoped src="../inventory/inventoryReport.css"></style>

<style scoped>
.pm-icon {
  display: inline-flex;
  flex: 0 0 30px;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: color-mix(in srgb, var(--pm) 14%, #fff);
  color: var(--pm);
}

.pm-share {
  min-width: 90px;
}

.pm-share .rp-bar-track {
  height: 5px;
}
</style>
